/**
 * Race-oppsett (buss vs. bil): bygger de to kjøretøyene, følgemarkørene og
 * scene8-driveren, og legger dem på kartet. Deles av dev-siden (main.js) og
 * race-embedden, så fartsprofiler og distanser bor ETT sted.
 *
 * Rutene (`route-bus`/`route-car`-kildene) må allerede være lagt til med
 * addRoutes() før denne kalles.
 */

import { ROUTE_SAMPLERS, ROUTE_COLORS } from './route.js';
import { createVehicleLayer } from './vehicle.js';
import { createMarkerLayer } from './markers.js';
import { createScene8 } from './scene8.js';

// Fartsprofiler (stykkevis konstant fart langs ruta). Distansene er hentet
// fra de prosesserte rutene (npm run routes):
//   buss gangstrekk @16656 m, bil Sandvika @~8500 m, bil gangstrekk @21525 m.
const WALK_SPEED = 1.4;        // m/s, delt gangfart (buss + bil)
const BUS_SPEED = 7.7496;      // m/s (~28 km/h), jevnt hele veien → buss 41.00 min
const BUS_WALK_START = 16656;  // m
const CAR_FAST = 22;           // m/s (~79 km/h) til Sandvika
const CAR_SLOW = 4.9591;       // m/s (~18 km/h) Sandvika → sentrum → bil 56.00 min
const CAR_SANDVIKA = 8500;     // m
const CAR_WALK_START = 21525;  // m
const BUS_START_DELAY_SEC = 0;  // begge starter samtidig (eksakte totaltider)

/** MM:SS av et ms-tall. */
export function formatClock(ms) {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * @param {object} opts
 * @param {import('maplibre-gl').Map} opts.map
 * @param {number} opts.exaggeration        terreng-eksagerering
 * @param {string} opts.busUrl              GLB-URL for bussen
 * @param {string} opts.carUrl              GLB-URL for bilen
 * @param {(realMs:number)=>void} [opts.onTime]  simulert klokke pr. frame
 * @param {string} [opts.busId='race-bus']
 * @param {string} [opts.carId='race-car']
 * @returns {{ scene8:object, busVehicle:object, carVehicle:object,
 *            busMarker:object, carMarker:object }}
 */
export function setupRace({
  map,
  exaggeration = 1,
  busUrl,
  carUrl,
  onTime,
  busId = 'race-bus',
  carId = 'race-car',
}) {
  const busVehicle = createVehicleLayer({
    id: busId, url: busUrl, sampler: ROUTE_SAMPLERS.bus,
    scale: 1, exaggeration, minzoom: 11,
  });
  busVehicle.setHidden(true);
  map.addLayer(busVehicle);

  const busMarker = createMarkerLayer({
    id: `${busId}-marker`, mode: 'bus',
    lngLat: ROUTE_SAMPLERS.bus.at(0).lngLat, color: ROUTE_COLORS.bus.line,
    heightAboveM: 100, headRadius: 4.4, hideAboveZoom: 15,
  });
  map.addLayer(busMarker);
  busMarker.setVisible(false);

  const carVehicle = createVehicleLayer({
    id: carId, url: carUrl, sampler: ROUTE_SAMPLERS.car,
    scale: 1, exaggeration, minzoom: 11,
  });
  carVehicle.setHidden(true);
  map.addLayer(carVehicle);

  const carMarker = createMarkerLayer({
    id: `${carId}-marker`, mode: 'car',
    lngLat: ROUTE_SAMPLERS.car.at(0).lngLat, color: ROUTE_COLORS.car.line,
    heightAboveM: 100, headRadius: 4.4, hideAboveZoom: 15,
  });
  map.addLayer(carMarker);
  carMarker.setVisible(false);

  const scene8 = createScene8({
    map,
    exaggeration,
    onTime,
    vehicles: [
      {
        mode: 'bus', sampler: ROUTE_SAMPLERS.bus,
        vehicle: busVehicle, marker: busMarker,
        startDelaySec: BUS_START_DELAY_SEC,
        phases: [
          { untilDist: BUS_WALK_START, speed: BUS_SPEED },
          { untilDist: Infinity, speed: WALK_SPEED },
        ],
      },
      {
        mode: 'car', sampler: ROUTE_SAMPLERS.car,
        vehicle: carVehicle, marker: carMarker,
        phases: [
          { untilDist: CAR_SANDVIKA, speed: CAR_FAST },
          { untilDist: CAR_WALK_START, speed: CAR_SLOW },
          { untilDist: Infinity, speed: WALK_SPEED },
        ],
      },
    ],
  });

  return { scene8, busVehicle, carVehicle, busMarker, carMarker };
}
